import React, { useState } from 'react';
import { Box, Typography, Paper, Alert } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ro from 'date-fns/locale/ro';
import { format } from 'date-fns';
import { useAuth } from '@contexts/AuthContext';
import { useReceptionData } from './hooks/useReceptionData';
import { StatsBar } from './components/StatsBar';
import { FiltersBar } from './components/FiltersBar';
import { AppointmentTable } from './components/AppointmentTable';
import { PaginationBar } from './components/PaginationBar';
import { CheckinModal } from './components/CheckinModal';
import { StartConsultationModal } from './components/StartConsultationModal';
import { FinishVisitModal } from './components/FinishVisitModal';
import { CancelAppointmentModal } from './components/CancelAppointmentModal';
import type { Appointment } from '@app-types/reception';

export default function ReceptionAppointments() {
  const { user } = useAuth();
  const clinicId = user?.clinicId || '';
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const {
    appointments,
    stats,
    loading,
    error,
    filters,
    pagination,
    refresh,
    checkin,
    changeStatus,
    cancelAppointment,
    updateFilters,
    changePage,
    changePageSize
  } = useReceptionData(dateStr, clinicId);

  const [checkinModal, setCheckinModal] = useState<{ open: boolean; appointment: Appointment | null }>({ open: false, appointment: null });
  const [startModal, setStartModal] = useState<{ open: boolean; appointment: Appointment | null }>({ open: false, appointment: null });
  const [finishModal, setFinishModal] = useState<{ open: boolean; appointment: Appointment | null }>({ open: false, appointment: null });
  const [cancelModal, setCancelModal] = useState<{ open: boolean; appointment: Appointment | null }>({ open: false, appointment: null });

  const handleCheckin = (id: string) => {
    const a = appointments.find((x) => x.id === id);
    if (a) setCheckinModal({ open: true, appointment: a });
  };
  const handleStart = (id: string) => {
    const a = appointments.find((x) => x.id === id);
    if (a) setStartModal({ open: true, appointment: a });
  };
  const handleFinish = (id: string) => {
    const a = appointments.find((x) => x.id === id);
    if (a) setFinishModal({ open: true, appointment: a });
  };
  const handleCancel = (id: string) => {
    const a = appointments.find((x) => x.id === id);
    if (a) setCancelModal({ open: true, appointment: a });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>Programări</Typography>
        <Paper sx={{ p: 2, mb: 2 }}>
          <DatePicker
            label="Data"
            value={selectedDate}
            onChange={(d) => d && setSelectedDate(d)}
            format="dd/MM/yyyy"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
          />
        </Paper>
        <Box mb={2}><StatsBar stats={stats} loading={loading} /></Box>
        <Box mb={2}><FiltersBar filters={filters} onFiltersChange={updateFilters} doctors={[]} /></Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <AppointmentTable
          appointments={appointments}
          loading={loading}
          error={error}
          onCheckin={handleCheckin}
          onStartConsultation={handleStart}
          onFinish={handleFinish}
          onCancel={handleCancel}
        />
        {appointments.length > 0 && (
          <PaginationBar
            page={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onPageChange={changePage}
            onPageSizeChange={changePageSize}
          />
        )}
        <CheckinModal open={checkinModal.open} appointment={checkinModal.appointment} onClose={() => setCheckinModal({ open: false, appointment: null })} onConfirm={async (id) => { await checkin(id); setCheckinModal({ open: false, appointment: null }); refresh(); }} />
        <StartConsultationModal open={startModal.open} appointment={startModal.appointment} onClose={() => setStartModal({ open: false, appointment: null })} onConfirm={async (id) => { await changeStatus(id, 'in_consultation'); setStartModal({ open: false, appointment: null }); refresh(); }} />
        <FinishVisitModal open={finishModal.open} appointment={finishModal.appointment} onClose={() => setFinishModal({ open: false, appointment: null })} onConfirm={async (id) => { await changeStatus(id, 'finished'); setFinishModal({ open: false, appointment: null }); refresh(); }} />
        <CancelAppointmentModal open={cancelModal.open} appointment={cancelModal.appointment} onClose={() => setCancelModal({ open: false, appointment: null })} onConfirm={async (id, reason) => { await cancelAppointment(id, reason); setCancelModal({ open: false, appointment: null }); refresh(); }} />
      </Box>
    </LocalizationProvider>
  );
}
